import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiCreditCard, FiUsers, FiSettings, FiFileText, 
  FiBarChart2, FiLogOut, FiShield, FiDollarSign,
  FiHome, FiDatabase
} from 'react-icons/fi';
import { useAuth } from './context/AuthContext';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/admin-login');
  };

  const menuItems = [
    {
      id: 'payments',
      name: 'Payment Management',
      description: 'Process and review transactions',
      icon: <FiCreditCard className="h-5 w-5" />,
      route: '/adminpay'
    },
    {
      id: 'forms',
      name: 'Form Data',
      description: 'View and manage form submissions',
      icon: <FiFileText className="h-5 w-5" />,
      route: '/admin-forms'
    },
    {
      id: 'users',
      name: 'User Management',
      description: 'Manage user accounts',
      icon: <FiUsers className="h-5 w-5" />,
      route: '/admin-users'
    },
    {
      id: 'reports',
      name: 'Reports',
      description: 'Business insights',
      icon: <FiBarChart2 className="h-5 w-5" />,
      route: '/admin-reports'
    },
    {
      id: 'settings',
      name: 'Settings',
      description: 'Configure application',
      icon: <FiSettings className="h-5 w-5" />,
      route: '/admin-settings'
    },
    {
      id: 'security',
      name: 'Security',
      description: 'Access controls',
      icon: <FiShield className="h-5 w-5" />,
      route: '/admin-security'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-20 px-4 md:px-8 pb-12 mt-16">
      <div className="max-w-6xl mx-auto">
        {/* Dashboard Header */}
        <div className="bg-white rounded-lg border border-gray-200 mb-8 p-6 flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-xl md:text-2xl font-semibold text-gray-800">Admin Dashboard</h1>
            <p className="text-gray-500 text-sm mt-1">Administrator control panel</p>
          </div>
          
          <div className="mt-4 md:mt-0 flex space-x-2">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center px-3 py-1.5 border border-gray-200 rounded-md text-gray-600 bg-white hover:bg-gray-50 text-sm"
            >
              <FiHome className="mr-1.5" /> 
              Main Site
            </button>
            
            <button 
              onClick={handleLogout}
              className="flex items-center px-3 py-1.5 bg-white border border-gray-200 rounded-md text-gray-600 hover:bg-gray-50 text-sm"
            >
              <FiLogOut className="mr-1.5" /> 
              Sign Out
            </button>
          </div>
        </div>
        
        {/* Menu Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {menuItems.map((item) => (
            <div 
              key={item.id}
              onClick={() => navigate(item.route)}
              className="bg-white rounded-lg border border-gray-200 hover:border-green-300 hover:shadow-sm transition-all cursor-pointer"
            >
              <div className="p-5 flex items-start">
                <div className="flex-shrink-0 p-2 rounded-lg bg-green-50 text-green-600">
                  {item.icon}
                </div>
                <div className="ml-4">
                  <h3 className="text-base font-medium text-gray-800">{item.name}</h3>
                  <p className="text-gray-500 text-sm mt-1">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Quick Stats Section */}
        <div className="mt-8 bg-white rounded-lg border border-gray-200 p-5">
          <h2 className="text-lg font-medium text-gray-800 mb-5 flex items-center">
            <FiDatabase className="mr-2 text-green-600" />
            System Overview
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-md p-3 border border-gray-200">
              <div className="flex items-center">
                <div className="p-1.5 bg-green-100 rounded-md text-green-600">
                  <FiUsers className="h-4 w-4" />
                </div>
                <div className="ml-3">
                  <p className="text-xs text-gray-500">Active Users</p>
                  <h4 className="text-lg font-semibold text-gray-800">1,423</h4>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-md p-3 border border-gray-200">
              <div className="flex items-center">
                <div className="p-1.5 bg-green-100 rounded-md text-green-600">
                  <FiFileText className="h-4 w-4" />
                </div>
                <div className="ml-3">
                  <p className="text-xs text-gray-500">Form Submissions</p>
                  <h4 className="text-lg font-semibold text-gray-800">842</h4>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-md p-3 border border-gray-200">
              <div className="flex items-center">
                <div className="p-1.5 bg-green-100 rounded-md text-green-600">
                  <FiDollarSign className="h-4 w-4" />
                </div>
                <div className="ml-3">
                  <p className="text-xs text-gray-500">Revenue</p>
                  <h4 className="text-lg font-semibold text-gray-800">₹92,485</h4>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-md p-3 border border-gray-200">
              <div className="flex items-center">
                <div className="p-1.5 bg-green-100 rounded-md text-green-600">
                  <FiBarChart2 className="h-4 w-4" />
                </div>
                <div className="ml-3">
                  <p className="text-xs text-gray-500">Conversion Rate</p>
                  <h4 className="text-lg font-semibold text-gray-800">18.7%</h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;